import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function Features() {
  const { t } = useLang();

  return (
    <section className="relative py-20 md:py-36 bg-[#050505] overflow-hidden" data-testid="features-section">
      <div className="absolute -left-40 top-1/3 w-[500px] h-[500px] bg-[#E60000]/5 rounded-full blur-[180px]" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Editorial header, left aligned */}
        <div className="mb-14 md:mb-20 max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-12 h-[2px] bg-[#E60000]" />
            <span className="text-[#E60000] text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold font-body">{t.features.section_tag}</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-[-0.02em] font-semibold uppercase text-white leading-[0.9]" data-testid="features-title">
            {t.features.title}{" "}<span className="text-[#E60000]">{t.features.title_accent}</span>
          </h2>
        </div>

        {/* Numbered manifesto chapters */}
        <div className="border-t border-white/10">
          {t.features.items.map((f, i) => {
            const num = String(i + 1).padStart(2, "0");
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="group relative grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start py-8 md:py-12 border-b border-white/10 transition-colors duration-500 hover:bg-white/[0.02]"
                data-testid={`feature-${i}`}
              >
                {/* Giant chapter number */}
                <div className="md:col-span-3">
                  <span className="font-heading text-6xl md:text-8xl font-semibold leading-none text-stroke-red group-hover:text-[#E60000] transition-colors duration-500">
                    {num}
                  </span>
                </div>

                {/* Title */}
                <div className="md:col-span-4">
                  <h3 className="font-heading text-2xl md:text-3xl font-medium uppercase text-white tracking-tight leading-tight group-hover:translate-x-1 transition-transform duration-500">
                    {f.title}
                  </h3>
                </div>

                {/* Description */}
                <div className="md:col-span-5 flex items-start justify-between gap-6">
                  <p className="font-body text-sm md:text-base text-white/60 leading-relaxed max-w-md">
                    {f.desc}
                  </p>
                  <ArrowUpRight className="w-6 h-6 text-white/20 group-hover:text-[#E60000] group-hover:rotate-45 transition-all duration-500 shrink-0 hidden md:block" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
